import { GridOptions, ValueFormatterParams } from 'ag-grid-community'
import {
  ColumnType,
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { ClientSideNumberFilter } from '../../../containers/BulkSheetView/components/filter'
import _ from 'lodash'
import { intl } from '../../../../i18n'
import { SequenceNoCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/sequenceNo/SequenceNoCellRenderer'
import numberCellStyle from '../../../containers/commons/AgGrid/components/cell/common/number/cellStyle'
import { RequestStatus } from '../taskActualWorkApprovalStatus'
import { DISCREPANCY_CELL_STYLE } from '../../ActualWorkingHours/gridOptions'
import {
  getRequestStatusLabel,
  numberFormatter,
} from '../../ProjectTaskActualWorkApprovalStatus/gridOptions'
import IconCellRenderer from '../../../containers/commons/AgGrid/components/cell/common/iconCell'
import { DecimalAndSexagesimalScale } from '../../../../lib/functions/decimalAndSexagesimalScale'
import { convertDecimalHourToHM } from '../../../../utils/time'
import { floatValueFormatter } from '../../../page-properties/bulksheet-properties/common'

const isSexagesimal = (scale: DecimalAndSexagesimalScale) => {
  return scale === DecimalAndSexagesimalScale.SEXAGESIMAL
}

const floatFormatter = (params: ValueFormatterParams) => {
  if (params.value === undefined || params.value === null) return ''
  return convertDecimalHourToHM(params.value) || ''
}

const defaultFloatFormatter = (params: ValueFormatterParams) => {
  if (params.value === undefined || params.value === null) return ''
  return floatValueFormatter(params, '', 2)
}

export const TaskActualWorkApprovalStatusGridOptions = (
  pageDecimalScaleUnit: DecimalAndSexagesimalScale
): GridOptions => {
  return {
    groupHeaderHeight: 25,
    headerHeight: 45,
    floatingFiltersHeight: 25,
    rowHeight: 32,
    treeData: false,
    context: {},
    suppressLastEmptyLineOnPaste: true,
    suppressContextMenu: true,
    // Row
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: false,
    enterMovesDownAfterEdit: true,
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColDef: {
      width: 85,
      editable: false,
      enableValue: false,
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressSizeToFit: true,
      singleClickEdit: false,
      cellEditor: 'textEditor',
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      suppressPaste: params => !_.isEmpty(params.context?.copied),
    },
    columnDefs: [
      {
        field: 'rowNumber',
        type: [ColumnType.sequenceNo],
        resizable: false,
        width: 35,
        cellRenderer: SequenceNoCellRenderer,
        valueGetter: params => {
          return (params.node?.rowIndex ?? 0) + 1
        },
      },
      {
        field: 'companyName',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.companyName',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 130,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'division',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.division',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 120,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'businessPartner',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.employeeType',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 120,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        valueGetter: params => {
          return params.data?.businessPartner
            ? intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.businessPartner',
              })
            : intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.fullTimeEmployee',
              })
        },
      },
      {
        field: 'userName',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.userName',
        }),
        hide: false,
        cellStyle: { justifyContent: 'left' },
        width: 120,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        cellRenderer: IconCellRenderer,
        cellRendererParams: {
          labelField: 'userName',
          iconUrlField: 'userIconUrl',
        },
        floatingFilter: true,
        valueGetter: params => {
          return params.data?.userName || ''
        },
      },
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.inputConfirmation',
        }),
        children: [
          {
            field: 'discrepancyTaskActualWorkingHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.taskActualWorkHours',
            }),
            hide: false,
            width: 100,
            pinned: true,
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            cellStyle: params => {
              return params.data?.discrepancyActualWorkingHour
                ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
                : { justifyContent: 'left' }
            },
            valueGetter: params => {
              return params.data?.discrepancyActualWorkingHour
                ? intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.blank',
                  })
                : intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.entered',
                  })
            },
          },
          {
            field: 'discrepancyActualWorkingHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.actualWorkingHours',
            }),
            hide: false,
            width: 80,
            pinned: true,
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            cellStyle: params => {
              return params.data?.discrepancyActualWorkingHour
                ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
                : { justifyContent: 'left' }
            },
            valueGetter: params => {
              return params.data?.discrepancyActualWorkingHour
                ? intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.blank',
                  })
                : intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.entered',
                  })
            },
          },
        ],
      },
      {
        field: 'requestStatus',
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.approvalStatus',
        }),
        hide: false,
        width: 110,
        pinned: true,
        lockPosition: 'left',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        cellStyle: params =>
          params.data?.requestStatus &&
          (params.data.requestStatus === RequestStatus.UNAPPROVED ||
            params.data.requestStatus === RequestStatus.UNREQUESTED)
            ? { ...DISCREPANCY_CELL_STYLE, justifyContent: 'left' }
            : { justifyContent: 'left' },
        valueGetter: params => {
          return getRequestStatusLabel(params.data?.requestStatus)
        },
      },
      // all projects
      {
        headerName: intl.formatMessage({
          id: 'taskActualWorkApprovalStatus.statusForAllProjects',
        }),
        children: [
          {
            field: 'scheduledWorkingDays',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.workingDays',
            }),
            hide: false,
            width: 80,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'absences',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.holidayDays',
            }),
            hide: false,
            width: 80,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'workingDays',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.attendanceDays',
            }),
            hide: false,
            width: 80,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: numberFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'totalWorkingTime',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.attendanceTime',
            }),
            hide: false,
            width: 80,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: isSexagesimal(pageDecimalScaleUnit)
              ? floatFormatter
              : defaultFloatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'totalActualWorkingHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.actualTime',
            }),
            hide: false,
            width: 80,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: isSexagesimal(pageDecimalScaleUnit)
              ? floatFormatter
              : defaultFloatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'requestedHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.requestedTime',
            }),
            hide: false,
            width: 80,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: isSexagesimal(pageDecimalScaleUnit)
              ? floatFormatter
              : defaultFloatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unRequestedHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unRequestedTime',
            }),
            hide: false,
            width: 90,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: isSexagesimal(pageDecimalScaleUnit)
              ? floatFormatter
              : defaultFloatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'approvedHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.approvedTime',
            }),
            hide: false,
            width: 90,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: isSexagesimal(pageDecimalScaleUnit)
              ? floatFormatter
              : defaultFloatFormatter,
            cellStyle: numberCellStyle,
          },
          {
            field: 'unApprovedHour',
            headerName: intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.unApprovedTime',
            }),
            hide: false,
            width: 90,
            filter: ClientSideNumberFilter,
            floatingFilter: true,
            valueFormatter: isSexagesimal(pageDecimalScaleUnit)
              ? floatFormatter
              : defaultFloatFormatter,
            cellStyle: numberCellStyle,
          },
        ],
      },
    ],
  }
}
